// UCAT Specialty Landing Page — Editorial redesign
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/Header/Header';
import ScrollToTopButton from '../../../components/ScrollToTopButton/ScrollToTopButton';
import SeoMeta from '../../../components/SeoMeta/SeoMeta';
import UcatEditorialFooter from './UcatEditorialFooter';
import './UcatLandingPage.css';

import { getBaseApiUrl, getEduPortalUrl } from '../../../config/api';
import { checkoutRefusalMessage, showCheckoutRefusal } from '../../checkout/checkoutRefusal';
import { ORG_PLAN_NOTICE, useStoredUserMayPurchase } from '../../checkout/purchaseEligibility';

// Reusable horizontal scroll-carousel shell (matches the swipeable card
// pattern used across the admissions matrix, method cards, sample
// question and school case-study sections).
function ScrollCarousel({ children, ariaLabel }) {
  const scrollRef = useRef(null);
  const [dotIndex, setDotIndex] = useState(0);
  const [showPrev, setShowPrev] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const count = React.Children.count(children);

  const cardStep = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !el.firstChild || !el.firstChild.firstElementChild) return 0;
    const card = el.firstChild.firstElementChild;
    return card.getBoundingClientRect().width + 14;
  }, []);

  const updateUI = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollLeft = el.scrollLeft;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const step = cardStep();
    const activeIndex = step ? Math.round(scrollLeft / step) : 0;
    setDotIndex(activeIndex);
    setShowPrev(scrollLeft > 4);
    setShowNext(scrollLeft < maxScroll - 4);
  }, [cardStep]);

  useEffect(() => {
    updateUI();
    const el = scrollRef.current;
    if (!el) return undefined;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { updateUI(); ticking = false; });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateUI);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateUI);
    };
  }, [updateUI]);

  const scrollByStep = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * cardStep(), behavior: 'smooth' });
  };

  return (
    <div className="ucat-scroll-carousel">
      <button
        type="button"
        className={`ucat-carousel-arrow ucat-arrow-prev ${showPrev ? 'show' : ''}`}
        onClick={() => scrollByStep(-1)}
        aria-label={`Previous ${ariaLabel}`}
      >
        ←
      </button>
      <button
        type="button"
        className={`ucat-carousel-arrow ucat-arrow-next ${showNext ? 'show' : ''}`}
        onClick={() => scrollByStep(1)}
        aria-label={`Next ${ariaLabel}`}
      >
        →
      </button>
      <div className="ucat-card-scroll" ref={scrollRef}>
        <div className="ucat-card-track">
          {children}
        </div>
      </div>
      <div className="ucat-swipe-dots">
        {Array.from({ length: count }).map((_, i) => (
          <span key={i} className={i === dotIndex ? 'active' : ''} />
        ))}
      </div>
    </div>
  );
}

export default function UcatLandingPage() {
  const navigate = useNavigate();
  const mayPurchase = useStoredUserMayPurchase();
  const [activeFaq, setActiveFaq] = useState(null);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const handleExplorePlans = () => {
    const el = document.getElementById('pricing') || document.getElementById('ucat-pricing');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubscribe = async (tierName) => {
    const tierSlug = tierName.toLowerCase().includes('beyond') ? 'ucat_beyond' : (tierName.toLowerCase().includes('foundation') ? 'ucat_foundations' : 'ucat_beginnings');

    const token = localStorage.getItem('pathbuddy_access_token') || sessionStorage.getItem('pathbuddy_access_token') || localStorage.getItem('token');
    const userRaw = localStorage.getItem('pathbuddy_user') || sessionStorage.getItem('pathbuddy_user') || localStorage.getItem('user');
    const user = (token && userRaw) ? (typeof userRaw === 'string' && userRaw.startsWith('{') ? JSON.parse(userRaw) : { email: userRaw }) : null;

    if (!user || !user.email) {
      sessionStorage.setItem('pending_checkout_tier', tierSlug);
      sessionStorage.setItem('pending_checkout_exam', 'ucat');
      navigate(`/login?signup=true&tier=${encodeURIComponent(tierSlug)}&exam=ucat`);
      return;
    }

    if (tierSlug === 'ucat_beginnings') {
      window.location.href = getEduPortalUrl('/practice/ucat');
      return;
    }

    try {
      const apiUrl = getBaseApiUrl();
      const token2 = localStorage.getItem('pathbuddy_access_token') || sessionStorage.getItem('pathbuddy_access_token') || localStorage.getItem('token');

      const headers = { 'Content-Type': 'application/json' };
      if (token2) {
        headers['Authorization'] = `Bearer ${token2}`;
      }

      const res = await fetch(`${apiUrl}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          tier_name: tierSlug,
          exam_type: 'ucat',
          user_id: user?.id,
          email: user?.email
        })
      });
      const data = await res.json().catch(() => ({}));
      const refusal = checkoutRefusalMessage(res, data);
      if (refusal) {
        showCheckoutRefusal(refusal);
        return;
      }
      if (data.checkoutUrl || data.url) {
        window.location.href = data.checkoutUrl || data.url;
      } else {
        sessionStorage.setItem('pending_checkout_tier', tierSlug);
        sessionStorage.setItem('pending_checkout_exam', 'ucat');
        navigate(`/login?signup=true&tier=${encodeURIComponent(tierSlug)}&exam=ucat`);
      }
    } catch (err) {
      sessionStorage.setItem('pending_checkout_tier', tierSlug);
      sessionStorage.setItem('pending_checkout_exam', 'ucat');
      navigate(`/login?signup=true&tier=${encodeURIComponent(tierSlug)}&exam=ucat`);
    }
  };

  const ucatRequirements = [
    { uni: "University of Cambridge", course: "Medicine (MB BChir)", requirement: "Mandatory", score: "2900+ / Band 1-2", details: "Used alongside academic & interview scoring" },
    { uni: "University of Oxford", course: "Medicine (BM BCh)", requirement: "Mandatory", score: "2950+ / Band 1-2", details: "50% UCAT & 50% GCSE shortlisting ratio" },
    { uni: "Imperial College London", course: "Medicine (MBBS)", requirement: "Mandatory", score: "2850+ / Band 1-2", details: "Cutoff threshold for interview call" },
    { uni: "King's College London (KCL)", course: "Medicine (MBBS)", requirement: "Mandatory", score: "2880+ / Band 1-2", details: "Heavy UCAT weighting in selection" },
    { uni: "University College London (UCL)", course: "Medicine (MBBS)", requirement: "Mandatory", score: "2850+ / Band 1-2", details: "Mandatory entrance requirement" },
    { uni: "University of Bristol", course: "Medicine (MB ChB)", requirement: "Mandatory", score: "2910+ / Band 1-2", details: "100% UCAT score used for interview invites" },
    { uni: "University of Manchester", course: "Medicine (MBChB)", requirement: "Mandatory", score: "2750+ / Band 1-2", details: "Threshold cutoff + SJT Band check" },
    { uni: "University of Birmingham", course: "Medicine (MBChB)", requirement: "Mandatory", score: "2820+ / Band 1-2", details: "Scaled UCAT score combined with GCSEs" },
    { uni: "University of Edinburgh", course: "MBChB Medicine", requirement: "Mandatory", score: "2850+ / Band 1-2", details: "Academic & UCAT score grid ranking" },
    { uni: "University of Glasgow", course: "MBChB Medicine", requirement: "Mandatory", score: "2780+ / Band 1-2", details: "UCAT score ranking for interview selection" }
  ];

  const structureCards = [
    {
      tag: "Method",
      title: "The IVORI Method",
      meta: "Interact with Pedagogical AI",
      items: [
        "Trained across four foundations spanning argument analysis, mental-models reasoning, fast-and-frugal heuristics and moral-judgement theory, so it reasons through every UCAT subtest the way a clinician or examiner would",
        "Finds exactly where an inference or a judgement call stops being warranted, so you correct the actual gap — not just the final answer",
        <>The world's first <button type="button" className="ucat-inline-link ucat-link-btn" onClick={() => navigate('/method/ucat')}>pedagogical AI model</button>, built on the first <a href="https://ivorilab.com" target="_blank" rel="noopener noreferrer" className="ucat-inline-link">Foundation Decision World Model</a>, meaning the feedback comes from a system built for reasoning</>
      ]
    },
    {
      tag: "Four Subtests, One Discipline",
      title: "Four Subtests, Four Traditions",
      meta: "Trained on All Four Deliberately",
      items: [
        "Verbal Reasoning is trained on Toulmin's model of argument — whether a conclusion is actually warranted by the stated evidence",
        "Decision Making is trained on Johnson-Laird's mental models theory — building the full constraint set instead of stopping at the first fit",
        "Quantitative Reasoning is trained on Gigerenzer's fast-and-frugal heuristics — knowing when to estimate and when to calculate exactly",
        "Situational Judgement is trained on Rest's four-component model — recognizing, judging and then acting on a professionalism issue"
      ]
    },
    {
      tag: "Foundation I",
      title: "Toulmin's Model of Argument",
      meta: "Verbal Reasoning — 22 min (+1.5 min) / 44 Q",
      items: [
        "Separates a passage's claim from its grounds, its warrant and its qualifier, so a \"true / false / can't tell\" judgement can be defended rather than guessed",
        "Trains the specific habit of noticing an unstated assumption smuggled between the evidence and the conclusion, exactly the gap the sleep-and-headache sample question turns on",
        "Diagnostics tag a miss by which part of the argument broke — an overreached warrant, an ignored qualifier — not by the passage's topic"
      ]
    },
    {
      tag: "Foundation II",
      title: "Johnson-Laird's Mental Models",
      meta: "Decision Making — 37 min (+1.5 min) / 35 Q",
      items: [
        "Builds the complete set of constraints a puzzle actually implies, rather than accepting the first assignment that happens to fit",
        "Trains the check Johnson-Laird's account calls for: actively searching for an alternative model that breaks your current conclusion before you commit to it",
        "Maps directly onto constraint puzzles like the Amara/Ben/Chidi/Dara department problem in this guide's carousel"
      ]
    },
    {
      tag: "Foundation III",
      title: "Gigerenzer's Fast-and-Frugal Heuristics",
      meta: "Quantitative Reasoning — 26 min (+2 min) / 36 Q",
      items: [
        "Trains recognizing which numbers in a question actually matter before reaching for the on-screen calculator, rather than calculating everything in sight",
        "Builds the estimate-first instinct QR's 26 minutes and 36 questions demands, since exhaustive calculation on every item runs out the clock",
        "Diagnostics flag when a slower, fully-calculated route was chosen over a faster, equally valid estimate"
      ]
    },
    {
      tag: "Foundation IV",
      title: "Rest's Four-Component Model",
      meta: "Situational Judgement — 26 min (+1.5 min) / 69 Q",
      items: [
        "Separates noticing there's an ethical issue at all (moral sensitivity) from knowing the right response (moral judgement) from actually being willing to act on it (moral motivation)",
        "Trains each stage against real GMC guidance and NHS Constitution scenarios, not abstract ethical theory",
        "Reasoning tested privately, one scenario at a time, with the same rigor a Band 1 response actually requires"
      ]
    }
  ];

  const schoolCaseStudies = [
    {
      name: "Handsworth Grammar",
      contact: "Dom Bird · Deputy Head Teacher",
      body: "King Edward VI Handsworth Grammar School for Boys has run its Year 12 medicine applicants' UCAT preparation through IVORI for close to two years, sitting alongside GMC ethics case practice, MMI rehearsal and UCAS personal statement support.",
      detailLabel: "Partner Since",
      detailValue: "May 2024",
      detail2Label: "License",
      detail2Value: "Annual"
    },
    {
      name: "Clifton College",
      contact: "Clifton's Team · Careers Department",
      body: "Clifton College piloted IVORI with its Year 12-to-13 medicine cohort, narrowing the trial deliberately to UCAT subtest practice and MMI preparation ahead of Oxbridge and other top medical school applications.",
      detailLabel: "Cohort",
      detailValue: "Year 12 → Year 13",
      detail2Label: "Focus",
      detail2Value: "Medicine Admissions"
    },
    {
      name: "King Edward VI",
      contact: "Active Discussions",
      body: "Building on Handsworth Grammar's results, conversations are underway with the wider King Edward VI Foundation about extending UCAT preparation across its Birmingham grammar schools' medicine applicants.",
      detailLabel: "Status",
      detailValue: "In Discussion",
      detail2Label: "Scope",
      detail2Value: "Foundation-Wide"
    },
    {
      name: "Grammar Schools",
      contact: "School Group",
      body: "State grammar schools use IVORI to prepare full Year 12 and 13 medicine cohorts for the UCAT, running subtest practice and MMI readiness alongside statutory careers provision.",
      detailLabel: "Group",
      detailValue: "State Grammar Schools",
      detail2Label: "Focus",
      detail2Value: "UCAT Practice"
    },
    {
      name: "Independent Schools",
      contact: "School Group",
      body: "Independent schools deploy IVORI for UCAT preparation across Year 12 and 13, supporting a smaller number of medicine applicants heading toward Oxbridge and other competitive medical schools.",
      detailLabel: "Group",
      detailValue: "Independent Schools",
      detail2Label: "Focus",
      detail2Value: "UCAT Practice"
    }
  ];

  const faqs = [
    {
      q: "Which medical schools require the UCAT for 2026/2027 entry, and what does it test?",
      a: [
        "Every medical school on IVORI's admissions matrix — including Cambridge, Oxford, Imperial, UCL, King's, Bristol, Manchester, Birmingham, Edinburgh and Glasgow — requires the UCAT for Medicine entry. Requirements and target scores vary by school and admissions cycle, so always check the school's current admissions requirements.",
        "The UCAT tests verbal, decision-making, quantitative and situational-judgement reasoning under time pressure across its four subtests — Verbal Reasoning, Decision Making, Quantitative Reasoning and Situational Judgement — not subject knowledge recall."
      ]
    },
    {
      q: "What is IVORI's pedagogical method, and who's behind it?",
      a: [
        "Every question, worked solution and mark scheme is reviewed by clinicians, medical educators and admissions specialists, alongside educators with formal pedagogical training. That review process is what lets IVORI explain not just the right answer, but why every alternative fails.",
        "IVORI trains across four foundations built for the UCAT's four distinct subtests: Toulmin's model of argument for Verbal Reasoning, Johnson-Laird's mental models theory for Decision Making, Gigerenzer's fast-and-frugal heuristics for Quantitative Reasoning, and Rest's four-component model for Situational Judgement. Underneath all four sits IVORI's own foundation decision world model — proprietary technology researched and pioneered in-house by IVORI Lab, and, we believe, the world's first pedagogical AI model built on this kind of foundation.",
        "It's what allows IVORI to reason through a student's working the way an examiner would, rather than simply marking it right or wrong — across a verbal passage, a logic puzzle, a drug calculation or an ethics scenario alike."
      ]
    },
    {
      q: "How does IVORI's practice, grading and scoring work?",
      a: [
        "Verbal Reasoning, Decision Making and Quantitative Reasoning are each scored separately on a 300-900 scale, while Situational Judgement is scored in Bands 1 to 4, with Band 1 highest — so IVORI diagnoses each subtest on its own terms rather than presenting one blended number.",
        "Full mocks reproduce the UCAT's exact structure and timing: Verbal Reasoning at 22 minutes for 44 questions, Decision Making at 37 minutes for 35 questions, Quantitative Reasoning at 26 minutes for 36 questions with the official on-screen calculator, and Situational Judgement at 26 minutes for 69 questions."
      ]
    },
    {
      q: "Can I use IVORI for more than just UCAT preparation?",
      a: ["Yes. IVORI's reasoning practice is designed around transferable skills including argument evaluation, constraint-based decision making, fast numerical estimation and professional judgement. UCAT is one of several admissions preparation products within the IVORI ecosystem."]
    },
    {
      q: "Can I cancel my subscription at any time?",
      a: ["Yes. All subscriptions can be managed or cancelled in one click from your account profile with zero hidden fees or commitments."]
    }
  ];

  const host = window.location.hostname.toLowerCase();
  const onIvoriEdu = host === 'ucat.ivoriedu.com';
  const onIvoriAiEdu = host === 'ucat.edu.ivoriai.com';
  const ucatUrl = onIvoriEdu
    ? 'https://ucat.ivoriedu.com/'
    : onIvoriAiEdu
      ? 'https://ucat.edu.ivoriai.com/'
      : (host.includes('ucat') ? 'https://ucat.edu.ivoriai.com/' : 'https://ivoriai.com/edu/ucat');
  const eduHomeUrl = onIvoriEdu
    ? 'https://ivoriedu.com'
    : 'https://ivoriai.com/';

  return (
    <div className="ucat-landing-root">
      <SeoMeta
        title="UCAT Exam Prep 2026 - UCAT Practice Tests & Medical Admissions Simulator"
        description="Master the UCAT (University Clinical Aptitude Test) for UK Medical Schools with ivori realistic computer exam simulations, subtest drills, and complete solution breakdowns."
        keywords="UCAT exam prep 2026, UCAT practice test, UCAT medical simulator, Cambridge Medicine UCAT, Oxford Medicine UCAT, Imperial UCAT, UCAT subtests, VR DM QR AR SJT, ucat cut off scores 2026, ucat deciles and percentiles, ucat verbal reasoning tricks, ucat quantitative reasoning shortcuts, ucat situational judgement band 1, ucat past papers free"
        url={ucatUrl}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Course",
                "name": "UCAT Medical Admissions Test Preparation",
                "description": "Master the University Clinical Aptitude Test (UCAT) for UK Medical Schools with ivori timed computer-based exam simulations, subtest drills, and complete solution breakdowns.",
                "provider": { "@type": "Organization", "name": "ivori AI", "url": eduHomeUrl },
                "url": ucatUrl,
                "courseMode": "online",
                "inLanguage": "en-GB",
                "isAccessibleForFree": false,
                "educationalLevel": "A-Level / Undergraduate Admissions",
                "teaches": [
                  "Verbal Reasoning & Argument Evaluation",
                  "Decision Making & Constraint-Based Logic",
                  "Quantitative Reasoning & Fast Estimation",
                  "Situational Judgement & Professional Ethics"
                ]
              },
              {
                "@type": "FAQPage",
                "mainEntity": faqs.map((f) => ({
                  "@type": "Question",
                  "name": f.q,
                  "acceptedAnswer": { "@type": "Answer", "text": f.a.join(' ') }
                }))
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ivoriai.com" },
                  { "@type": "ListItem", "position": 2, "name": "UCAT Preparation", "item": ucatUrl }
                ]
              }
            ]
          })
        }}
      />
      <Header variant="exam-landing" />

      {/* HERO */}
      <section className="ucat-hero-section">
        <div className="ucat-container ucat-hero-content">
          <span className="ucat-mono-eyebrow">IVORI Edu</span>
          <h1 className="ucat-hero-title">Master the UCAT.</h1>
          <p className="ucat-hero-stat">80% of IVORI students score 700+ / 900 in their strongest subtest.</p>
          <p className="ucat-hero-tagline">Train the reasoning every UCAT subtest rewards.</p>
          <button type="button" className="ucat-hero-cta" onClick={handleExplorePlans}>
            Choose Tier
          </button>
        </div>
      </section>

      {/* ADMISSIONS GUIDE */}
      <section id="ucat-matrix" className="ucat-editorial-section">
        <div className="ucat-container">
          <div className="ucat-editorial-section-head">
            <span className="ucat-mono-eyebrow">Target Scores</span>
            <h2>UK Medical School UCAT Admissions Matrix for 2026/2027 Entry</h2>
            <p>Benchmark against Oxbridge, Imperial and other leading UK medical schools</p>
          </div>

          <ScrollCarousel ariaLabel="medical school">
            {ucatRequirements.map((row, idx) => (
              <div className="ucat-matrix-card" key={idx}>
                <div className="ucat-matrix-uni">{row.uni}</div>
                <div className="ucat-matrix-row"><span>Degree Program</span><b>{row.course}</b></div>
                <div className="ucat-matrix-row"><span>Requirement</span><b className="ucat-req-badge">{row.requirement}</b></div>
                <div className="ucat-matrix-row ucat-score-cell"><span>Target Score</span><b>{row.score}</b></div>
                <div className="ucat-matrix-row"><span>Admissions Note</span><b>{row.details}</b></div>
              </div>
            ))}
          </ScrollCarousel>
        </div>
      </section>

      {/* HOW IT WORKS / IVORI METHOD */}
      <section className="ucat-editorial-section">
        <div className="ucat-container">
          <div className="ucat-editorial-section-head">
            <span className="ucat-mono-eyebrow">How It Works</span>
            <h2>Utilize IVORI Edu</h2>
            <p>Every candidate sits all four UCAT subtests — Verbal Reasoning, Decision Making, Quantitative Reasoning and Situational Judgement — trained across four foundations built for exactly that split.</p>
          </div>

          <ScrollCarousel ariaLabel="section">
            {structureCards.map((card, idx) => (
              <div className="ucat-structure-card" key={idx}>
                <span className="ucat-mono-tag">{card.tag}</span>
                <h3>{card.title}</h3>
                <div className="ucat-structure-meta">{card.meta}</div>
                <ul>
                  {card.items.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
            ))}
          </ScrollCarousel>
        </div>
      </section>

      {/* SAMPLE QUESTION */}
      <section className="ucat-editorial-section">
        <div className="ucat-container">
          <div className="ucat-editorial-section-head">
            <span className="ucat-mono-eyebrow">Interactive Practice</span>
            <h2>Improve Your Score</h2>
            <p>See how IVORI works through a full worked example from each of the UCAT's four subtests</p>
          </div>

          <ScrollCarousel ariaLabel="card">
            <div className="ucat-qcard-panel">
              <span className="ucat-mono-tag">Verbal Reasoning</span>
              <div className="ucat-passage">
                Investigators surveyed 1,200 adults about their sleep habits and found that participants who reported fewer than six hours of sleep per night were three times more likely to report frequent headaches than those sleeping seven to nine hours. However, the researchers noted that participants with headaches were also more likely to be simultaneously managing high workloads, which independently disrupted their sleep.
              </div>
              <h3>Which of the following can be logically concluded from the passage?</h3>
            </div>

            <div className="ucat-qcard-panel">
              <span className="ucat-mono-tag">Answer Options</span>
              <div className="ucat-option">A) Sleeping fewer than six hours causes headaches.</div>
              <div className="ucat-option correct">B) High workload may be a contributing factor behind both reduced sleep and headache frequency.</div>
              <div className="ucat-option">C) Participants who slept seven to nine hours never experienced headaches.</div>
              <div className="ucat-option">D) The study proves a direct causal relationship between sleep and headache frequency.</div>
            </div>

            <div className="ucat-qcard-panel">
              <span className="ucat-mono-tag">Explanation</span>
              <p>The passage explicitly names high workload as a factor linked to both reduced sleep and headache frequency — exactly what option B states, without over-claiming.</p>
              <p>A and D assert a direct causation the passage never establishes as its warrant; C makes an absolute claim ("never") the data can't support. The correct answer is the one that stays inside what the stated evidence actually backs.</p>
            </div>

            <div className="ucat-qcard-panel">
              <span className="ucat-mono-tag">Decision Making</span>
              <h3>Four colleagues — Amara, Ben, Chidi and Dara — each work in a different department: Cardiology, Radiology, Neurology and Oncology.</h3>
              <p>· Amara does not work in Radiology or Oncology.<br />· Ben works in Neurology.<br />· Chidi works in the department that comes immediately before Dara's, alphabetically.</p>
              <h3>Which department does Chidi work in?</h3>
            </div>

            <div className="ucat-qcard-panel">
              <span className="ucat-mono-tag">Answer Options</span>
              <div className="ucat-option">A) Cardiology</div>
              <div className="ucat-option">B) Radiology</div>
              <div className="ucat-option">C) Neurology</div>
              <div className="ucat-option correct">D) Oncology</div>
            </div>

            <div className="ucat-qcard-panel">
              <span className="ucat-mono-tag">Explanation</span>
              <p>Amara is excluded from Radiology and Oncology, leaving Cardiology or Neurology — and Ben already holds Neurology, so Amara must be Cardiology.</p>
              <p>That leaves Radiology and Oncology for Chidi and Dara. Alphabetically, Oncology precedes Radiology, so Chidi — who holds the earlier department — is Oncology. Building the full constraint set first is what rules out Cardiology, Radiology and Neurology before an answer is committed to.</p>
            </div>

            <div className="ucat-qcard-panel">
              <span className="ucat-mono-tag">Quantitative Reasoning</span>
              <h3>A hospital pharmacy dispenses a drug in vials containing 250mg per 5ml. A patient is prescribed a 350mg dose, to be given as an infusion diluted at a 1:4 ratio (drug:diluent) by volume.</h3>
              <p>What is the total volume (in ml) of the final diluted solution?</p>
            </div>

            <div className="ucat-qcard-panel">
              <span className="ucat-mono-tag">Answer Options</span>
              <div className="ucat-option">A) 7 ml</div>
              <div className="ucat-option">B) 28 ml</div>
              <div className="ucat-option correct">C) 35 ml</div>
              <div className="ucat-option">D) 42 ml</div>
            </div>

            <div className="ucat-qcard-panel">
              <span className="ucat-mono-tag">Explanation</span>
              <p>Drug volume = (350 ÷ 250) × 5 = 7ml. At a 1:4 ratio, the diluent is four times the drug volume: 7 × 4 = 28ml.</p>
              <p>Total volume = 7 + 28 = 35ml. Spotting the two-step ratio structure fast — dose to drug volume, then drug volume to diluent — is faster than converting every unit before seeing where the calculation is actually heading.</p>
            </div>

            <div className="ucat-qcard-panel">
              <span className="ucat-mono-tag">Situational Judgement</span>
              <div className="ucat-passage">
                A junior doctor notices that a senior colleague has repeatedly been arriving to shifts smelling faintly of alcohol, though their clinical performance has not visibly suffered. The junior doctor is unsure whether to raise this formally.
              </div>
              <h3>Which of the following responses is the most appropriate first step?</h3>
            </div>

            <div className="ucat-qcard-panel">
              <span className="ucat-mono-tag">Answer Options</span>
              <div className="ucat-option">A) Ignore it, since the colleague's performance has not been affected.</div>
              <div className="ucat-option correct">B) Raise the concern directly and confidentially with the colleague, escalating to a senior or clinical governance lead if it continues.</div>
              <div className="ucat-option">C) Report the colleague anonymously to the media to protect patient safety.</div>
              <div className="ucat-option">D) Wait until a patient is harmed before taking any action.</div>
            </div>

            <div className="ucat-qcard-panel">
              <span className="ucat-mono-tag">Explanation</span>
              <p>GMC guidance places patient safety above collegiate loyalty, but proportionate escalation starts with a direct, confidential conversation and a support pathway — not with silence or with a public accusation.</p>
              <p>A and D leave a plausible safety risk unaddressed; C is disproportionate and bypasses every internal safeguard. Noticing the issue is moral sensitivity; choosing B over the other three is moral judgement; actually raising it despite the social discomfort is what moral motivation and character look like in practice.</p>
            </div>
          </ScrollCarousel>
        </div>
      </section>

      {/* SCHOOL CASE STUDIES */}
      <section className="ucat-editorial-section">
        <div className="ucat-container">
          <div className="ucat-editorial-section-head">
            <span className="ucat-mono-eyebrow">Used in Schools</span>
            <h2>Trusted by UK Schools</h2>
            <p>Deployed across top UK grammar and independent schools with medicine-track UCAT candidates</p>
          </div>

          <ScrollCarousel ariaLabel="school">
            {schoolCaseStudies.map((school, idx) => (
              <div className="ucat-school-card" key={idx}>
                <h3>{school.name}</h3>
                <div className="ucat-school-contact">{school.contact}</div>
                <p>{school.body}</p>
                <p className="ucat-school-more">
                  Find out more about{' '}
                  <button type="button" className="ucat-inline-link ucat-link-btn" onClick={() => navigate('/cases')}>
                    {school.name}
                  </button>
                </p>
                <div className="ucat-school-detail">
                  {school.detailLabel}: <b>{school.detailValue}</b> · {school.detail2Label}: <b>{school.detail2Value}</b>
                </div>
              </div>
            ))}
          </ScrollCarousel>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="ucat-editorial-section ucat-pricing-editorial">
        <div className="ucat-container">
          <div className="ucat-editorial-section-head">
            <span className="ucat-mono-eyebrow">Prep Tiers</span>
            <h2>Select Your Tier</h2>
            <p>Choose your level and go deeper when you're ready to improve your score</p>
            {!mayPurchase && <p>{ORG_PLAN_NOTICE}</p>}
          </div>

          <div className="ucat-pricing-grid">
            <div className="ucat-plan">
              <h3>Beginnings</h3>
              <div className="ucat-plan-price">
                <span className="amount">£0</span> <span className="period">free forever</span>
              </div>
              <button type="button" className="ucat-plan-btn" onClick={() => handleSubscribe('UCAT Beginnings')}>
                Get Started
              </button>
              <ul>
                <li><span className="ucat-check">·</span> <b>All Content for Free</b></li>
                <li><span className="ucat-check">·</span> 4 Official Tests / 182 Questions</li>
                <li><span className="ucat-check">·</span> 19 Easy Tests / 855 Questions</li>
                <li><span className="ucat-check">·</span> 350+ Medical Ethics &amp; MMI Interview Cases &amp; Tutorials</li>
              </ul>
            </div>

            <div className="ucat-plan">
              <h3>Foundations</h3>
              <div className="ucat-plan-price">
                <span className="amount">£60</span> <span className="period">12-month access</span>
              </div>
              {mayPurchase && (
                <button type="button" className="ucat-plan-btn" onClick={() => handleSubscribe('UCAT Foundations')}>
                  Get Started
                </button>
              )}
              <ul>
                <li><span className="ucat-check">·</span> <b>All content in Beginnings</b></li>
                <li><span className="ucat-check">·</span> 12 Standard Tests / 540 Questions</li>
                <li><span className="ucat-check">·</span> 2,500+ Question Bank Across All Four Subtests</li>
                <li><span className="ucat-check">·</span> UCAS Personal Statement Reviewer</li>
              </ul>
            </div>

            <div className="ucat-plan featured">
              <span className="ucat-plan-badge">Recommended · Oxbridge</span>
              <h3>Beyond</h3>
              <div className="ucat-plan-price">
                <span className="amount">£80</span> <span className="period">12-month access</span>
              </div>
              {mayPurchase && (
                <button type="button" className="ucat-plan-btn" onClick={() => handleSubscribe('UCAT Beyond')}>
                  Get Started
                </button>
              )}
              <ul>
                <li><span className="ucat-check">·</span> <b>All content in Foundations</b></li>
                <li><span className="ucat-check">·</span> 4 Hard Tests / 180 Questions</li>
                <li><span className="ucat-check">·</span> GMC Ethics &amp; NHS Constitution Case Scenarios</li>
                <li><span className="ucat-check">·</span> 16 Medical School MMI Interview Simulations</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="ucat-editorial-section ucat-faq-editorial">
        <div className="ucat-container">
          <div className="ucat-editorial-section-head">
            <span className="ucat-mono-eyebrow">Got Questions?</span>
            <h2>FAQs</h2>
            <p>Find answers on prep tiers, scoring and how IVORI's feedback works</p>
          </div>

          <div className="ucat-faq">
            {faqs.map((faq, index) => (
              <div key={index} className={`ucat-faq-item ${activeFaq === index ? 'open' : ''}`}>
                <div className="ucat-faq-q" onClick={() => toggleFaq(index)}>
                  <span>{faq.q}</span>
                  <span className="ucat-faq-plus">+</span>
                </div>
                <div className="ucat-faq-a">
                  {faq.a.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <UcatEditorialFooter />
      <ScrollToTopButton />
    </div>
  );
}